import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  Search,
  RefreshCw,
  Database,
  AlertCircle,
  CheckCircle,
  Navigation,
  BarChart3,
  Server,
  FlaskConical,
  Mail,
  Play,
  Filter,
  FileText,
  Users,
  Settings,
  Loader2,
  Sparkles
} from 'lucide-react';
import { initializeApp, getApps } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getFirestore, collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, runTransaction, serverTimestamp } from 'firebase/firestore';
import RoutingAdminConfig from './RoutingAdminConfig';
import BIDataSettings from './BIDataSettings';
import SystemSettings from './SystemSettings';
import RolesAndUsersPage from './RolesAndUsersPage';
import DirectoryPanel from '../../../platform/components/DirectoryPanel';
import AiUsagePanel from '../../../platform/components/AiUsagePanel';
import { canAdminTenant, callWorkGateway } from './shell/workGateway';
// CHA-276 — Carbon pass (Modules concept). Presentation only: RealLookupService,
// the Firestore/BigQuery CRUD split, validation, and all callables are identical.
import { SkeletonRows, NoResults } from './shell/carbonStates';

// Carbon class shorthands (chrome only)
const TH = 'px-3 py-2 font-plex-mono text-[10px] tracking-[0.12em] uppercase font-normal text-carbon-gray-50 border-b border-carbon-border';
const CHIP = 'font-plex-mono text-[9.5px] tracking-[0.08em] uppercase font-medium border px-2 py-0.5';
const INPUT = 'w-full px-3 py-2 text-sm bg-carbon-white text-carbon-ink border focus:outline-none focus:ring-1 focus:ring-carbon-blue-60';
const BTN_PRIMARY = 'px-4 py-2 text-xs font-medium bg-carbon-ink text-carbon-white hover:bg-carbon-gray-80 disabled:opacity-50';
const BTN_OUTLINE = 'px-4 py-2 text-xs font-medium bg-carbon-white border border-carbon-gray-30 text-carbon-ink hover:bg-carbon-gray-10 disabled:opacity-50';


// Service class for interacting with the backend.
// Firestore-backed tables (real-time CRUD). All others use BigQuery.
const FIRESTORE_TABLES = {
  email_distribution_list: { orderByField: 'name' },
  warehouse_fees: { orderByField: 'warehouse' },
  us_holidays: { orderByField: 'date' },
  lookup_hts_tariff_reference: { orderByField: 'htsDigits' }, // CHA-297 — HTS duty/tariff reference
  lookup_royalty_rates: { orderByField: 'brand' }, // CHA-303 — royalty rates + ERP program linking
  lookup_broker_invoice: { orderByField: 'commercial_invoice' }, // CHA-316 — broker# ↔ commercial-invoice bridge
  lookup_first_sale_vendors: { orderByField: 'vendor_name' }, // CHA-362 — First Sale vendor program
  lookup_locations: { orderByField: 'code' }, // CHA-404 — ERP location master (supersedes lc_beneficiary_codes)
  lookup_target_factories: { orderByField: 'location_id', docIdField: 'location_id', tenantScoped: true } // CHA-578 — Target factory/location master
};

const ADMIN_LOOKUP_CALLABLE_TABLES = new Set([
  'email_distribution_list',
  'warehouse_fees',
  'us_holidays',
  'lookup_hts_tariff_reference',
  'lookup_royalty_rates',
  'lookup_first_sale_vendors',
  'lookup_locations',
]);

function firestoreCollectionRef(db, tableName, fsConfig, tenantId) {
  if (fsConfig?.tenantScoped) {
    return collection(db, 'tenants', tenantId, tableName);
  }
  return collection(db, tableName);
}

function firestoreDocRef(db, tableName, fsConfig, tenantId, id) {
  if (fsConfig?.tenantScoped) {
    return doc(db, 'tenants', tenantId, tableName, id);
  }
  return doc(db, tableName, id);
}

export class RealLookupServiceClass {
  constructor() {
    this.functions = null;
    this.db = null;
    this.initialized = false;
  }

  async initialize() {
    if (this.initialized) return;
    try {
      if (getApps().length === 0) {
        const firebaseConfig = typeof window.__firebase_config !== 'undefined' ? JSON.parse(window.__firebase_config) : {};
        initializeApp(firebaseConfig);
      }
      this.functions = getFunctions();
      this.db = getFirestore();
      this.initialized = true;
      console.log('✅ RealLookupService initialized with Cloud Functions + Firestore');
    } catch (error) {
      console.error('❌ Failed to initialize RealLookupService:', error);
      throw error;
    }
  }

  /**
   * Get all records from a lookup table
   */
  async getTableData(tableName, tenantId) {
    await this.initialize();
    try {
      const fsConfig = FIRESTORE_TABLES[tableName];
      if (fsConfig) {
        if (fsConfig.tenantScoped && !tenantId) throw new Error('tenantId is required');
        const q = query(firestoreCollectionRef(this.db, tableName, fsConfig, tenantId), orderBy(fsConfig.orderByField));
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      }
      const getLookupTableData = httpsCallable(this.functions, 'getLookupTableData');
      const result = await getLookupTableData({ tableName });
      return result.data.data || [];
    } catch (error) {
      console.error(`❌ Error getting table data for ${tableName}:`, error);
      throw new Error(`Failed to get data from ${tableName}: ${error.message}`);
    }
  }

  /**
   * Add a new record to a lookup table
   */
  async addRecord(tableName, record, tenantId) {
    await this.initialize();
    try {
      const fsConfig = FIRESTORE_TABLES[tableName];
      if (fsConfig) {
        if (fsConfig.tenantScoped && !tenantId) throw new Error('tenantId is required');
        if (tableName === 'lookup_target_factories') {
          const manageTargetFactory = httpsCallable(this.functions, 'manageTargetFactory');
          const result = await manageTargetFactory({ action: 'create', tenantId, record });
          return result.data.entry;
        }
        if (tableName === 'lookup_broker_invoice') {
          const manageBrokerInvoiceLookup = httpsCallable(this.functions, 'manageBrokerInvoiceLookup');
          const result = await manageBrokerInvoiceLookup({ action: 'admin_create', tenantId, record });
          return result.data.entry;
        }
        if (ADMIN_LOOKUP_CALLABLE_TABLES.has(tableName)) {
          const manageAdminLookup = httpsCallable(this.functions, 'manageAdminLookup');
          const result = await manageAdminLookup({ action: 'create', tableName, tenantId, record });
          return result.data.entry;
        }
        if (fsConfig.docIdField) {
          const docId = String(record[fsConfig.docIdField] || '').trim();
          if (!docId) {
            throw new Error(`${fsConfig.docIdField} is required`);
          }
          const docRef = firestoreDocRef(this.db, tableName, fsConfig, tenantId, docId);
          const normalized = {
            ...record,
            [fsConfig.docIdField]: docId,
            tenantId,
            managedBy: 'operator'
          };
          await runTransaction(this.db, async (transaction) => {
            const existing = await transaction.get(docRef);
            if (existing.exists()) {
              throw new Error(`Record ${docId} already exists in ${tableName}`);
            }
            transaction.set(docRef, {
              ...normalized,
              created_at: serverTimestamp(),
              updated_at: serverTimestamp()
            });
          });
          return { id: docId, ...normalized };
        }
        const docRef = await addDoc(firestoreCollectionRef(this.db, tableName, fsConfig, tenantId), {
          ...record,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp()
        });
        return { id: docRef.id, ...record };
      }
      const addLookupRecord = httpsCallable(this.functions, 'addLookupRecord');
      const result = await addLookupRecord({ tableName, record });
      return result.data.data;
    } catch (error) {
      console.error(`❌ Error adding record to ${tableName}:`, error);
      throw new Error(`Failed to add record to ${tableName}: ${error.message}`, { cause: error.details });
    }
  }

  /**
   * Update an existing record in a lookup table
   */
  async updateRecord(tableName, id, record, tenantId) {
    await this.initialize();
    try {
      const fsConfig = FIRESTORE_TABLES[tableName];
      if (fsConfig) {
        if (fsConfig.tenantScoped && !tenantId) throw new Error('tenantId is required');
        if (tableName === 'lookup_target_factories') {
          const manageTargetFactory = httpsCallable(this.functions, 'manageTargetFactory');
          const result = await manageTargetFactory({ action: 'update', tenantId, locationId: id, record });
          return result.data.entry;
        }
        if (tableName === 'lookup_broker_invoice') {
          const manageBrokerInvoiceLookup = httpsCallable(this.functions, 'manageBrokerInvoiceLookup');
          const result = await manageBrokerInvoiceLookup({ action: 'admin_update', tenantId, id, record });
          return result.data.entry;
        }
        if (ADMIN_LOOKUP_CALLABLE_TABLES.has(tableName)) {
          const manageAdminLookup = httpsCallable(this.functions, 'manageAdminLookup');
          const result = await manageAdminLookup({ action: 'update', tableName, tenantId, id, record });
          return result.data.entry;
        }
        if (fsConfig.docIdField && record[fsConfig.docIdField] !== undefined) {
          const requestedId = String(record[fsConfig.docIdField]).trim();
          if (requestedId !== String(id)) {
            throw new Error(`${fsConfig.docIdField} cannot be changed after creation`);
          }
        }
        const docRef = firestoreDocRef(this.db, tableName, fsConfig, tenantId, id);
        await updateDoc(docRef, { ...record, updated_at: serverTimestamp() });
        return { id, ...record };
      }
      const updateLookupRecord = httpsCallable(this.functions, 'updateLookupRecord');
      const result = await updateLookupRecord({ tableName, id, record });
      return result.data.data.updatedRecord;
    } catch (error) {
      console.error(`❌ Error updating record in ${tableName}:`, error);
      throw new Error(`Failed to update record in ${tableName}: ${error.message}`, { cause: error.details });
    }
  }

  /**
   * Delete a record from a lookup table
   */
  async deleteRecord(tableName, id, tenantId) {
    await this.initialize();
    try {
      const fsConfig = FIRESTORE_TABLES[tableName];
      if (fsConfig) {
        if (fsConfig.tenantScoped && !tenantId) throw new Error('tenantId is required');
        if (tableName === 'lookup_target_factories') {
          const manageTargetFactory = httpsCallable(this.functions, 'manageTargetFactory');
          await manageTargetFactory({ action: 'delete', tenantId, locationId: id });
          return true;
        }
        if (tableName === 'lookup_broker_invoice') {
          const manageBrokerInvoiceLookup = httpsCallable(this.functions, 'manageBrokerInvoiceLookup');
          await manageBrokerInvoiceLookup({ action: 'admin_delete', tenantId, id });
          return true;
        }
        if (ADMIN_LOOKUP_CALLABLE_TABLES.has(tableName)) {
          const manageAdminLookup = httpsCallable(this.functions, 'manageAdminLookup');
          await manageAdminLookup({ action: 'delete', tableName, tenantId, id });
          return true;
        }
        await deleteDoc(firestoreDocRef(this.db, tableName, fsConfig, tenantId, id));
        return true;
      }
      const deleteLookupRecord = httpsCallable(this.functions, 'deleteLookupRecord');
      const result = await deleteLookupRecord({ tableName, id });
      return result.data.data.success;
    } catch (error) {
      console.error(`❌ Error deleting record from ${tableName}:`, error);
      throw new Error(`Failed to delete record from ${tableName}: ${error.message}`, { cause: error.details });
    }
  }

  /**
   * Initialize lookup tables in BigQuery
   */
  async initializeSampleData() {
    await this.initialize();
    try {
      const initializeLookupTables = httpsCallable(this.functions, 'initializeLookupTables');
      const result = await initializeLookupTables();
      return result.data.data.success;
    } catch (error) {
      console.error('❌ Error initializing BigQuery lookup tables:', error);
    }
  }
}

const RealLookupService = new RealLookupServiceClass();

// ✅-- UPDATED: Added new tables as requested.
const LOOKUP_TABLES = {
  division_name: {
    name: 'Division Names',
    tableName: 'division_name',
    description: 'Product division categories',
    fields: [
      { key: 'name', label: 'Division Name', type: 'text', required: true, placeholder: 'Enter division name' },
      { key: 'code', label: 'Division Code', type: 'text', required: true, placeholder: 'Enter 4-letter code' },
      { key: 'description', label: 'Description', type: 'textarea', required: false, placeholder: 'Enter description' },
      { key: 'active', label: 'Active', type: 'boolean', required: false }
    ],
    primaryKey: 'id'
  },
  
  country_codes: {
    name: 'Country Codes',
    tableName: 'country_codes',
    description: 'Country reference data',
    fields: [
      { key: 'country_name', label: 'Country Name', type: 'text', required: true, placeholder: 'Enter country name' },
      { key: 'iso_code_2', label: 'ISO Code (2)', type: 'text', required: true, placeholder: 'Enter 2-letter code' },
      { key: 'iso_code_3', label: 'ISO Code (3)', type: 'text', required: false, placeholder: 'Enter 3-letter code' },
      { key: 'region', label: 'Region', type: 'text', required: false, placeholder: 'Enter region' },
      { key: 'active', label: 'Active', type: 'boolean', required: false }
    ],
    primaryKey: 'id'
  },
  
  currency_types: {
    name: 'Currency Types',
    tableName: 'currency_types',
    description: 'Currency reference data',
    fields: [
      { key: 'currency_name', label: 'Currency Name', type: 'text', required: true, placeholder: 'Enter currency name' },
      { key: 'currency_code', label: 'Currency Code', type: 'text', required: true, placeholder: 'Enter currency code' },
      { key: 'symbol', label: 'Symbol', type: 'text', required: false, placeholder: 'Enter currency symbol' },
      { key: 'exchange_rate', label: 'Exchange Rate', type: 'number', required: false, placeholder: 'Enter exchange rate' },
      { key: 'active', label: 'Active', type: 'boolean', required: false }
    ],
    primaryKey: 'id'
  },
  
  vendor_data: {
    name: 'Vendor Data',
    tableName: 'vendor_data',
    description: 'Manage vendor and factory information',
    fields: [
      { key: 'factory_code', label: 'Factory Code', type: 'text', required: true, placeholder: 'Enter factory code' },
      { key: 'factory_name', label: 'Factory Name', type: 'text', required: true, placeholder: 'Enter factory name' },
      { key: 'wm_id_number', label: 'WM ID Number', type: 'text', required: false, placeholder: 'Enter Walmart ID' },
      { key: 'city', label: 'City', type: 'text', required: false, placeholder: 'Enter city' },
      { key: 'state', label: 'State', type: 'text', required: false, placeholder: 'Enter state/province' },
      { key: 'country', label: 'Country', type: 'text', required: false, placeholder: 'Enter country' },
      { key: 'beneficiary_code', label: 'Beneficiary Code', type: 'text', required: false, placeholder: 'Enter beneficiary code' },
      { key: 'beneficiary_name', label: 'Beneficiary Name', type: 'text', required: false, placeholder: 'Enter beneficiary name' },
      { key: 'active', label: 'Active', type: 'boolean', required: false }
    ],
    primaryKey: 'id'
  },
  
  // 🎯 JUST ADD NEW TABLES HERE - NO BACKEND CHANGES NEEDED!
  factories: {
    name: 'Factories',
    tableName: 'factories',
    description: 'Manage production factory information',
    fields: [
      { key: 'factory_name', label: 'Factory Name', type: 'text', required: true, placeholder: 'Enter factory name' },
      { key: 'factory_code', label: 'Factory Code', type: 'text', required: true, placeholder: 'Internal factory code' },
      { key: 'country', label: 'Country', type: 'text', required: false, placeholder: 'e.g., Vietnam' },
      { key: 'contact_person', label: 'Contact Person', type: 'text', required: false, placeholder: 'Primary contact name' },
      { key: 'active', label: 'Active', type: 'boolean', required: false }
    ],
    primaryKey: 'id'
  },

  target_factories: {
    name: 'Target Factories',
    tableName: 'lookup_target_factories',
    description: 'Target factory/location master from the Target address update workbook. Location ID is Target factory/location identifier; inactive rows are locations marked closed or no longer used.',
    fields: [
      { key: 'location_id', label: 'Location ID', type: 'text', required: true, placeholder: 'Target location ID', pattern: '^[1-9]\\d*$', patternHint: 'Location ID must be a positive integer without leading zeros' },
      { key: 'factory_name', label: 'Factory Name', type: 'text', required: true, placeholder: 'Factory name' },
      { key: 'address', label: 'Address', type: 'textarea', required: false, placeholder: 'Full address' },
      { key: 'address_line1', label: 'Address Line 1', type: 'text', required: false },
      { key: 'address_line2', label: 'Address Line 2', type: 'text', required: false },
      { key: 'city', label: 'City', type: 'text', required: false },
      { key: 'state', label: 'State / Province', type: 'text', required: false },
      { key: 'postal_code', label: 'Postal Code', type: 'text', required: false },
      { key: 'country', label: 'Country', type: 'text', required: false },
      { key: 'validation_status', label: 'Validation Status', type: 'select', required: true, placeholder: 'Select status', options: ['VALIDATED', 'UPDATED', 'NEEDS_REVIEW', 'PENDING_CHECK', 'NO_LONGER_USE', 'CLOSED'] },
      { key: 'target_note', label: 'Target Note', type: 'textarea', required: false, placeholder: 'Workbook update/validation note' },
      { key: 'source_file', label: 'Source File', type: 'text', required: true, defaultValue: 'Admin Lookup Manager', placeholder: 'Workbook or manual source' },
      { key: 'active', label: 'Active', type: 'boolean', required: false }
    ],
    validateRecord: (record) => {
      const status = record.validation_status;
      if ((status === 'CLOSED' || status === 'NO_LONGER_USE') && record.active !== false) {
        return { active: `${status} rows must be inactive` };
      }
      return {};
    },
    primaryKey: 'id'
  },
  
  // 🚀 EXAMPLE: Add any new table easily!
  customers: {
    name: 'Customers',
    tableName: 'customers',
    description: 'Customer master data',
    fields: [
      { key: 'customer_name', label: 'Customer Name', type: 'text', required: true, placeholder: 'Enter customer name' },
      { key: 'customer_code', label: 'Customer Code', type: 'text', required: true, placeholder: 'Enter customer code' },
      { key: 'email', label: 'Email', type: 'text', required: false, placeholder: 'Enter email address' },
      { key: 'phone', label: 'Phone', type: 'text', required: false, placeholder: 'Enter phone number' },
      { key: 'credit_limit', label: 'Credit Limit', type: 'number', required: false, placeholder: 'Enter credit limit' },
      { key: 'active', label: 'Active', type: 'boolean', required: false }
    ],
    primaryKey: 'id'
  },
  
  products: {
    name: 'Products',
    tableName: 'products',
    description: 'Product catalog management',
    fields: [
      { key: 'product_name', label: 'Product Name', type: 'text', required: true, placeholder: 'Enter product name' },
      { key: 'sku', label: 'SKU', type: 'text', required: true, placeholder: 'Enter SKU' },
      { key: 'category', label: 'Category', type: 'text', required: false, placeholder: 'Enter category' },
      { key: 'unit_price', label: 'Unit Price', type: 'number', required: false, placeholder: 'Enter unit price' },
      { key: 'weight_kg', label: 'Weight (kg)', type: 'number', required: false, placeholder: 'Enter weight' },
      { key: 'active', label: 'Active', type: 'boolean', required: false }
    ],
    primaryKey: 'id'
  },

  wm_fineline_department: {
    name: 'Walmart Fineline Department',
    tableName: 'wm_fineline_department',
    description: 'Walmart fineline to department mapping',
    fields: [
      { key: 'fineline_number', label: 'Fineline Number', type: 'text', required: true, placeholder: 'Enter fineline number' },
      { key: 'department_number', label: 'Department Number', type: 'text', required: true, placeholder: 'Enter department number' },
      { key: 'fineline_description', label: 'Fineline Description', type: 'textarea', required: false, placeholder: 'Enter fineline description' }
    ],
    primaryKey: 'id'
  },

  importer_codes: {
    name: 'Importer Codes',
    tableName: 'importer_codes',
    description: 'Maps importer number to importer name. Joins on imports_data_source_1.importer_number.',
    fields: [
      { key: 'importer_number', label: 'Importer Number', type: 'text', required: true, placeholder: 'e.g., 13-413953300' },
      { key: 'importer_name', label: 'Importer Name', type: 'text', required: true, placeholder: 'e.g., InMocean Group' },
      { key: 'active', label: 'Active', type: 'boolean', required: false }
    ],
    primaryKey: 'id'
  },

  tariff_regime_codes: {
    name: 'Tariff Regime Codes',
    tableName: 'tariff_regime_codes',
    description: 'HTSUS Chapter 99/98 tariff regime lookup. Codes not in this list default to "2018 Tariff".',
    fields: [
      { key: 'htsus_code', label: 'HTSUS Code', type: 'text', required: true, placeholder: 'e.g., 99030172' },
      { key: 'tariff_regime', label: 'Tariff Regime', type: 'text', required: true, placeholder: 'e.g., IEEPA Reciprocal (Vietnam)' },
      { key: 'tag', label: 'Tag', type: 'select', required: true, placeholder: 'Select tag', options: ['Fentanyl', 'Reciprocal', '2018 Tariff', 'Other'] },
      { key: 'brief_description', label: 'Brief Description', type: 'textarea', required: false, placeholder: 'Short description' },
      { key: 'active', label: 'Active', type: 'boolean', required: false }
    ],
    primaryKey: 'id'
  },

  warehouse_fees: {
    name: 'Warehouse Fees',
    tableName: 'warehouse_fees',
    description: 'Warehouse fee schedule by warehouse, charge type, and vendor.',
    fields: [
      { key: 'warehouse', label: 'Warehouse', type: 'text', required: true, placeholder: 'e.g., ETWL' },
      { key: 'charge_type', label: 'Type of Charge', type: 'text', required: true, placeholder: 'e.g., Storage, Handling, Pick & Pack' },
      { key: 'unit_of_measure', label: 'Unit of Measure', type: 'text', required: true, placeholder: 'e.g., Per Pallet, Per Case, Per Piece' },
      { key: 'amount', label: 'Amount', type: 'number', required: true, placeholder: '0.00' },
      { key: 'vendor_name', label: 'Vendor Name', type: 'text', required: true, placeholder: 'e.g., Eastern Warehouse' },
      { key: 'active', label: 'Active', type: 'boolean', required: false }
    ],
    primaryKey: 'id'
  },

  us_holidays: {
    name: 'US Holidays',
    tableName: 'us_holidays',
    description: 'US federal holidays for previous business day calculation. Add dates once per year.',
    fields: [
      { key: 'date', label: 'Date', type: 'text', required: true, placeholder: 'YYYY-MM-DD (e.g., 2026-01-01)' },
      { key: 'name', label: 'Holiday Name', type: 'text', required: true, placeholder: 'e.g., New Years Day' },
      { key: 'year', label: 'Year', type: 'text', required: true, placeholder: 'e.g., 2026' }
    ],
    primaryKey: 'id'
  },

  lookup_hts_tariff_reference: {
    name: 'HTS Tariff Reference',
    tableName: 'lookup_hts_tariff_reference',
    description: 'Reference duty % + tariff % by 10-digit HTS (from the IMA_HTS_Codes workbook). PERCENT numbers (free/excluded → 0). Seeded by functions/scripts/seed-hts-tariff-reference.js; feeds the Production → Verification reference checks.',
    fields: [
      { key: 'htsDigits', label: 'HTS (digits)', type: 'text', required: true, placeholder: '10 digits, e.g., 3304100000', pattern: '^\\d{10}$', patternHint: 'HTS (digits) must be exactly 10 digits — the reference check joins on this key' },
      { key: 'htsDisplay', label: 'HTS (display)', type: 'text', required: false, placeholder: 'e.g., 3304.10.0000' },
      { key: 'dutyPct', label: 'Duty %', type: 'number', required: true, placeholder: 'e.g., 6.5' },
      { key: 'tariffPct', label: 'Tariff %', type: 'number', required: true, placeholder: 'e.g., 25' },
      { key: 'description', label: 'Description', type: 'textarea', required: false, placeholder: 'Product description' },
      { key: 'note', label: 'Note', type: 'textarea', required: false, placeholder: 'Provenance / exclusion notes' }
    ],
    primaryKey: 'id'
  },

  lookup_royalty_rates: {
    name: 'Royalty Rates',
    tableName: 'lookup_royalty_rates',
    description: 'Royalty % by brand (blank channel/category = All), from the operator\'s royalty workbook. ERP Programs = the Blue Cherry mg4_pgm codes this brand covers (comma-separated — one brand may map to several). Seeded by functions/scripts/seed-royalty-rates.js; will feed the Royalty check (phase 2).',
    fields: [
      { key: 'brand', label: 'Brand', type: 'text', required: true, placeholder: 'e.g., Hurley' },
      { key: 'channel', label: 'Channel (blank = All)', type: 'text', required: false, placeholder: 'e.g., Off Price' },
      { key: 'category', label: 'Category (blank = All)', type: 'text', required: false, placeholder: 'e.g., Swim' },
      { key: 'usPct', label: 'US %', type: 'number', required: true, placeholder: 'e.g., 10' },
      { key: 'canadaPct', label: 'Canada %', type: 'number', required: true, placeholder: 'e.g., 12' },
      { key: 'erpPrograms', label: 'ERP Programs (mg4_pgm, comma-separated)', type: 'text', required: false, placeholder: 'e.g., HURLEY (comma-separate if several)' },
      { key: 'note', label: 'Note', type: 'textarea', required: false, placeholder: 'Linking notes' }
    ],
    primaryKey: 'id'
  },

  lookup_broker_invoice: {
    name: 'Broker ↔ Invoice',
    tableName: 'lookup_broker_invoice',
    description: 'Commercial invoice → broker job # (the bridge from your customs broker files to the ACE duty feed). Seeded from the west-coast broker report; edit here to fix the ~10% typos / off-value invoices and add missing links. job # is B-prefixed (matches ACE).',
    fields: [
      { key: 'commercial_invoice', label: 'Commercial Invoice', type: 'text', required: true, placeholder: 'e.g., A25127' },
      { key: 'job_number', label: 'Broker Job # (B-prefixed)', type: 'text', required: true, placeholder: 'e.g., B00322908' },
      { key: 'note', label: 'Note', type: 'textarea', required: false, placeholder: 'e.g., typo fix / manual link' }
    ],
    primaryKey: 'id'
  },

  lookup_locations: {
    name: 'Locations (ERP master)',
    tableName: 'lookup_locations',
    description: 'The Blue Cherry location master (CHA-404) — every LC beneficiary, factory, agent and customer code with its company name and address. This is what turns a bare code like DONFEN into "Dongguan Fengshang Daily Necessities Co., Ltd" across First Sale and Power BI. Refreshed from the BCADMIN export (ERP wins on refresh; codes absent from an export are reported, never deleted). Which factories sit under a beneficiary is NOT stored here — it is observed from live orders on Imports → First Sale → Beneficiaries.',
    fields: [
      { key: 'code', label: 'Code', type: 'text', required: true, placeholder: 'e.g., DONFEN' },
      { key: 'name', label: 'Company Name', type: 'text', required: true, placeholder: 'e.g., Dongguan Fengshang Daily Necessities Co., Ltd' },
      { key: 'address1', label: 'Address 1', type: 'text', required: false, placeholder: 'Street' },
      { key: 'address2', label: 'Address 2', type: 'text', required: false, placeholder: 'District / area' },
      { key: 'address3', label: 'Address 3', type: 'text', required: false },
      { key: 'city', label: 'City', type: 'text', required: false },
      { key: 'state', label: 'State', type: 'text', required: false },
      { key: 'zip', label: 'Zip', type: 'text', required: false },
      { key: 'country', label: 'Country', type: 'text', required: false, placeholder: '2-letter code, e.g., CN' },
      { key: 'loc_type', label: 'Loc Type', type: 'text', required: false, placeholder: 'ERP Loc Type (A / C / V / R; comma-joined if several)' },
      { key: 'currency', label: 'Currency', type: 'text', required: false, placeholder: 'e.g., USD' },
      { key: 'active', label: 'Active', type: 'boolean', required: false }
    ],
    primaryKey: 'id'
  },

  lookup_first_sale_vendors: {
    name: 'First Sale Vendors',
    tableName: 'lookup_first_sale_vendors',
    description: 'First Sale program tracking per vendor (CHA-362). BC Code(s) are the ERP lc_beneficiary values — comma-separate multiples (e.g., JIAGUO,JIAGGG,JIAGUU). Approved = Yes makes the vendor count in the FS/FS3 compliance check on Imports → First Sale → Vendors.',
    fields: [
      { key: 'vendor_name', label: 'Vendor Name', type: 'text', required: true, placeholder: 'e.g., ANF' },
      { key: 'status', label: 'Status', type: 'text', required: true, placeholder: 'Complete / In progress / Waiting for documents' },
      { key: 'approved', label: 'Approved?', type: 'text', required: true, placeholder: 'Yes / No' },
      { key: 'comments', label: 'Comments', type: 'textarea', required: false, placeholder: 'Program notes' },
      { key: 'letter_filed', label: 'First Sale Notification Letter Filed?', type: 'text', required: false, placeholder: 'Yes / (blank)' },
      { key: 'bc_codes', label: 'BC Code(s)', type: 'text', required: false, placeholder: 'e.g., ANFPTE or JIAGUO,JIAGGG,JIAGUU' }
    ],
    primaryKey: 'id'
  },

  email_distribution_list: {
    name: 'Email Distribution List',
    tableName: 'email_distribution_list',
    description: 'Manage email recipients for automated reports (PO Managers, Sales, etc.)',
    fields: [
      { key: 'name', label: 'Name', type: 'text', required: true, placeholder: 'Enter full name' },
      { key: 'role', label: 'Role', type: 'select', required: true, placeholder: 'Select role', options: ['PO Manager', 'Sales Person', 'Operations', 'Finance', 'Executive', 'Other'] },
      { key: 'code', label: 'Code', type: 'text', required: true, placeholder: 'Code to match in reports (e.g., PO Manager code)' },
      { key: 'group_name', label: 'Group', type: 'text', required: false, placeholder: 'Enter group name (e.g., Division, Team)' },
      { key: 'direct_email', label: 'Direct Email', type: 'text', required: true, placeholder: 'Enter direct email address' },
      { key: 'group_email', label: 'Group Email', type: 'text', required: false, placeholder: 'Enter group email (optional)' },
      { key: 'active', label: 'Active', type: 'boolean', required: false }
    ],
    primaryKey: 'id'
  }
};

function RecordForm({ table, record, onSave, onCancel, isEditing }) {
  const [formData, setFormData] = useState(record || {});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEditing) {
        const initialData = {};
        table.fields.forEach(field => {
            if (field.type === 'boolean') {
                initialData[field.key] = true;
            }
            if (field.defaultValue !== undefined) {
                initialData[field.key] = field.defaultValue;
            }
        });
        setFormData(prev => ({ ...initialData, ...prev }));
    }
  }, [isEditing, table.fields]);


  const validateForm = () => {
    const newErrors = {};
    table.fields.forEach(field => {
      // CHA-297 (§6.3 Major 2) — 0 is a real value for required numeric fields (49/93 HTS reference
      // records carry a 0 duty or tariff: 'free'/'excluded' normalize to 0). Only empty is missing.
      const v = formData[field.key];
      if (field.required && (v === undefined || v === null || v === '')) {
        newErrors[field.key] = `${field.label} is required`;
      }
      // CHA-297 — the reference check joins on a 10-digit key; a malformed key would be silently
      // skipped by the engine loader, so block it at the form instead.
      if (field.pattern && v !== undefined && v !== null && v !== '' && !new RegExp(field.pattern).test(String(v))) {
        newErrors[field.key] = field.patternHint || `${field.label} has an invalid format`;
      }
    });
    if (typeof table.validateRecord === 'function') {
      Object.assign(newErrors, table.validateRecord(formData));
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      setSaving(true);
      try {
        await onSave(formData);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleInputChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: null }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 font-plex text-carbon-ink">
      <div className="bg-carbon-white border border-carbon-border max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-carbon-border bg-carbon-gray-10">
          <h3 className="text-[15px] font-semibold text-carbon-ink m-0">
            {isEditing ? 'Edit Record' : 'Add New Record'}
          </h3>
          <p className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 m-0 mt-0.5">{table.name}</p>
        </div>

        <div className="p-6 space-y-4">
          {table.fields.map(field => (
            <div key={field.key}>
              <label className="block font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mb-1">
                {field.label}
                {field.required && <span className="text-carbon-red-60 ml-1">*</span>}
              </label>

              {field.type === 'text' && (
                <input
                  type="text"
                  value={formData[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  disabled={saving}
                  className={`${INPUT} ${
                    errors[field.key] ? 'border-carbon-red-60' : 'border-carbon-gray-30'
                  } ${saving ? 'bg-carbon-gray-10' : ''}`}
                />
              )}

              {field.type === 'number' && (
                <input
                  type="number"
                  step="0.01"
                  value={formData[field.key] ?? ''}
                  onChange={(e) => {
                    // CHA-297 (§6.3 Major 2) — `parseFloat(x) || ''` turned a typed 0 into '' (blocked
                    // duty-free/tariff-free rows). Keep 0; only non-numeric input clears the field.
                    const n = parseFloat(e.target.value);
                    handleInputChange(field.key, Number.isFinite(n) ? n : '');
                  }}
                  placeholder={field.placeholder}
                  disabled={saving}
                  className={`${INPUT} font-plex-mono ${
                    errors[field.key] ? 'border-carbon-red-60' : 'border-carbon-gray-30'
                  } ${saving ? 'bg-carbon-gray-10' : ''}`}
                />
              )}

              {field.type === 'textarea' && (
                <textarea
                  value={formData[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  placeholder={field.placeholder}
                  rows={3}
                  disabled={saving}
                  className={`${INPUT} ${
                    errors[field.key] ? 'border-carbon-red-60' : 'border-carbon-gray-30'
                  } ${saving ? 'bg-carbon-gray-10' : ''}`}
                />
              )}

              {field.type === 'boolean' && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={!!formData[field.key]}
                    onChange={(e) => handleInputChange(field.key, e.target.checked)}
                    disabled={saving}
                    className="h-4 w-4 text-carbon-blue-60 focus:ring-carbon-blue-60 border-carbon-gray-30"
                  />
                  <span className="ml-2 text-sm text-carbon-gray-70">Enabled</span>
                </div>
              )}

              {field.type === 'select' && (
                <select
                  value={formData[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  disabled={saving}
                  className={`${INPUT} ${
                    errors[field.key] ? 'border-carbon-red-60' : 'border-carbon-gray-30'
                  } ${saving ? 'bg-carbon-gray-10' : ''}`}
                >
                  <option value="">{field.placeholder || 'Select...'}</option>
                  {field.options?.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              )}

              {errors[field.key] && (
                <p className="text-xs text-carbon-red-60 mt-1">{errors[field.key]}</p>
              )}
            </div>
          ))}

          <div className="flex justify-end gap-3 pt-4 border-t border-carbon-border">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className={BTN_OUTLINE}
            >
              <X className="h-4 w-4 inline mr-1" />
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className={`${BTN_PRIMARY} flex items-center`}
            >
              {saving ? (
                <RefreshCw className="h-4 w-4 inline mr-1 animate-spin" />
              ) : (
                <Save className="h-4 w-4 inline mr-1" />
              )}
              {saving ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LookupTableManager({ tableKey, tenantId }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [error, setError] = useState(null);

  const table = LOOKUP_TABLES[tableKey];
  const isReadOnly = table.readOnly === true;

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tableData = await RealLookupService.getTableData(table.tableName, tenantId);
      setData(Array.isArray(tableData) ? tableData : []);

      // Debug: Log actual column names for wm_fineline_department
      if (table.tableName === 'wm_fineline_department' && tableData && tableData.length > 0) {
        console.log('🔍 DEBUG - Actual columns in wm_fineline_department:', Object.keys(tableData[0]));
        console.log('🔍 DEBUG - Sample row:', tableData[0]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError(`Failed to load ${table.name}: ${error.message}`);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [table.tableName, table.name, tenantId]);

  useEffect(() => {
    loadData();
  }, [loadData, tableKey]);

  const handleAdd = () => {
    if (isReadOnly) return;
    setEditingRecord(null);
    setShowForm(true);
  };

  const handleEdit = (record) => {
    if (isReadOnly) return;
    setEditingRecord(record);
    setShowForm(true);
  };

  const handleSave = async (formData) => {
    if (isReadOnly) return;
    setError(null);
    try {
      if (editingRecord) {
        const updated = await RealLookupService.updateRecord(
          table.tableName,
          editingRecord[table.primaryKey],
          formData,
          tenantId
        );
        setData(prev => prev.map(item =>
          item[table.primaryKey] === updated.id ? { ...item, ...updated } : item
        ));
      } else {
        const created = await RealLookupService.addRecord(table.tableName, formData, tenantId);
        setData(prev => [created, ...prev]);
      }
      setShowForm(false);
      setEditingRecord(null);
    } catch (error) {
      console.error('Error saving record:', error);
      const errorMessage = error.message || '';

      // Check for streaming buffer error
      if (errorMessage.includes('streaming buffer')) {
        setError(`Cannot ${editingRecord ? 'update' : 'add'}: This table has data in BigQuery's streaming buffer. Please wait 90-120 minutes for the buffer to clear, or contact support to recreate the table.`);
      } else {
        const detailedMessage = error.cause ? JSON.stringify(error.cause) : error.message;
        setError(`Failed to save record: ${detailedMessage}`);
      }
    }
  };

  const handleDelete = async (record) => {
    if (isReadOnly) return;
    setError(null);
    try {
      await RealLookupService.deleteRecord(table.tableName, record[table.primaryKey], tenantId);
      setData(prev => prev.filter(item => item[table.primaryKey] !== record[table.primaryKey]));
      setDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting record:', error);
      const errorMessage = error.message || '';

      // Check for streaming buffer error
      if (errorMessage.includes('streaming buffer')) {
        setError(`Cannot delete: This table has data in BigQuery's streaming buffer. Please wait 90-120 minutes for the buffer to clear, or contact support to recreate the table.`);
      } else {
        const detailedMessage = error.cause ? JSON.stringify(error.cause) : error.message;
        setError(`Failed to delete record: ${detailedMessage}`);
      }
    }
  };

  const filteredData = Array.isArray(data) ? data.filter(record => record && typeof record === 'object').filter(record =>
        Object.values(record).some(value =>
          value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        )
      ) : [];

  if (loading) {
    return (
      <div className="bg-carbon-white border border-carbon-border font-plex">
        <SkeletonRows rows={6} />
      </div>
    );
  }

  return (
    <div className="space-y-5 font-plex text-carbon-ink">
      {error && (
        <div className="relative bg-carbon-white border border-carbon-border px-[18px] py-3 flex items-start gap-3">
          <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-carbon-red-60" />
          <AlertCircle size={15} className="text-carbon-red-60 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="text-[13px] font-semibold text-carbon-ink">An error occurred:</span>
            <p className="text-xs text-carbon-gray-70 break-all m-0">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-carbon-gray-50 hover:text-carbon-ink flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h2 className="text-[15px] font-semibold text-carbon-ink m-0">{table.name}</h2>
          <p className="text-xs text-carbon-gray-50 m-0 mt-0.5">
            {table.description}
            {isReadOnly && (
              <span className="ml-2 font-plex-mono text-[10px] tracking-[0.08em] uppercase text-carbon-gray-50">
                Read-only
              </span>
            )}
          </p>
        </div>
        {!isReadOnly && (
          <button
            onClick={handleAdd}
            className={`${BTN_PRIMARY} flex items-center gap-2`}
          >
            <Plus className="h-4 w-4" />
            Add Record
          </button>
        )}
      </div>

      <div className="bg-carbon-white border border-carbon-border overflow-hidden">
        {/* Filter bar attached to the table top (Orders anatomy) */}
        <div className="px-[18px] py-2.5 border-b border-carbon-border flex items-center justify-between gap-4 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-carbon-gray-40 h-4 w-4" />
            <input
              type="text"
              placeholder="Search records..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`${INPUT} pl-10 border-carbon-gray-30`}
            />
          </div>
          <div className="flex items-center gap-3">
            <span className="font-plex-mono text-[10.5px] tracking-[0.08em] uppercase text-carbon-gray-50">
              Total: <span className="font-semibold text-carbon-ink">{filteredData.length}</span>
            </span>
            <span className="font-plex-mono text-[10.5px] tracking-[0.08em] uppercase text-carbon-gray-50">
              Active: <span className="font-semibold text-carbon-ink">{filteredData.filter(r => r.active !== false).length}</span>
            </span>
            <button
              onClick={loadData}
              className={`${BTN_OUTLINE} flex items-center gap-1.5`}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr>
                {table.fields.map(field => (
                  <th key={field.key} className={`${TH} text-left`}>
                    {field.label}
                  </th>
                ))}
                <th className={`${TH} text-left`}>
                  Status
                </th>
                {!isReadOnly && (
                  <th className={`${TH} text-right sticky right-0 z-20 bg-carbon-gray-10 border-l border-carbon-border`}>
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredData.map((record) => (
                <tr key={record[table.primaryKey]} className="group border-b border-carbon-gray-10 hover:bg-carbon-gray-10">
                  {table.fields.map(field => (
                    <td
                      key={field.key}
                      // CHA-403 — free text is capped + truncated (full value on hover) instead of an
                      // unbounded nowrap; short codes keep nowrap so they never wrap mid-value.
                      className={`px-3 py-2 ${field.type === 'textarea' ? 'max-w-[26rem] truncate' : 'whitespace-nowrap'} ${field.type === 'number' ? 'font-plex-mono text-[11.5px]' : 'text-[13px]'} text-carbon-ink`}
                      title={field.type === 'textarea' && record[field.key] ? String(record[field.key]) : undefined}
                    >
                      {field.type === 'boolean' ? (
                        record[field.key] ? (
                          <CheckCircle className="h-4 w-4 text-carbon-green-50" />
                        ) : (
                          <X className="h-4 w-4 text-carbon-gray-40" />
                        )
                      ) : (
                        record[field.key] || '-'
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`${CHIP} ${
                      record.active !== false
                        ? 'border-carbon-green-50 text-carbon-green-50'
                        : 'border-carbon-gray-30 text-carbon-gray-50'
                    }`}>
                      {record.active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {!isReadOnly && (
                    <td className="px-3 py-2 whitespace-nowrap text-right sticky right-0 z-10 bg-carbon-white group-hover:bg-carbon-gray-10 border-l border-carbon-border">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(record)}
                          className="text-carbon-gray-50 hover:text-carbon-blue-60 p-1 hover:bg-carbon-gray-10"
                        >
                          <Edit3 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(record)}
                          className="text-carbon-gray-50 hover:text-carbon-red-60 p-1 hover:bg-carbon-gray-10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredData.length === 0 && (
          searchTerm ? (
            <NoResults query={searchTerm} onClear={() => setSearchTerm('')} />
          ) : (
            <div className="text-center py-12">
              <Database className="mx-auto h-10 w-10 text-carbon-gray-40" />
              <h3 className="mt-2 text-sm font-semibold text-carbon-ink">No records found</h3>
              <p className="mt-1 text-xs text-carbon-gray-50">
                {isReadOnly ? 'No records are available for this read-only lookup.' : 'Get started by adding a new record'}
              </p>
            </div>
          )
        )}
      </div>

      {showForm && !isReadOnly && (
        <RecordForm
          table={table}
          record={editingRecord}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingRecord(null);
          }}
          isEditing={!!editingRecord}
        />
      )}

      {deleteConfirm && !isReadOnly && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 font-plex text-carbon-ink">
          <div className="bg-carbon-white border border-carbon-border max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="h-5 w-5 text-carbon-red-60" />
                <h3 className="text-[15px] font-semibold text-carbon-ink m-0">Confirm Delete</h3>
              </div>
              <p className="text-sm text-carbon-gray-70 mb-6">
                Are you sure you want to delete this record? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className={BTN_OUTLINE}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(deleteConfirm)}
                  className="px-4 py-2 text-xs font-medium text-carbon-white bg-carbon-red-60 hover:opacity-90"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminLookupManager({ userProfile, tenantId } = {}) { // CHA-289 — threaded to SystemSettings (tenantArg pattern)
  useEffect(() => {
    try {
        const firebaseConfig = typeof window.__firebase_config !== 'undefined'
            ? JSON.parse(window.__firebase_config)
            : {};

        if (!getApps().length) {
            initializeApp(firebaseConfig);
            console.log("🔥 Firebase initialized successfully for AdminLookupManager.");
        }
    } catch (e) {
        console.error("🚨 Failed to initialize Firebase in AdminLookupManager:", e);
    }
  }, []);

  const [selectedTable, setSelectedTable] = useState('division_name');
  const [activeSection, setActiveSection] = useState('lookups'); // 'lookups', 'routing', 'actualcost', 'firstsale', 'bidata', or 'system'
  const resolvedTenantId = tenantId || userProfile?.tenantId;

  return (
    <div className="space-y-5 font-plex text-carbon-ink">
      {/* Header — Carbon (CHA-276): title + underline section tabs + ink lookup chips */}
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold tracking-[-0.01em] m-0">Admin</h1>
            <div className="font-plex-mono text-[10px] tracking-[0.12em] uppercase text-carbon-gray-50 mt-0.5">
              Lookups · roles · sync · system
            </div>
          </div>
        </div>

        {/* Section Tabs — Carbon underline */}
        <div className="mt-4 flex border-b border-carbon-border overflow-x-auto">
          {[
            { id: 'lookups', label: 'Lookup Tables', icon: Database },
            { id: 'roles', label: 'Roles & Users', icon: Users },
            { id: 'routing', label: 'Routing Sync', icon: Navigation },
            { id: 'bidata', label: 'BI Data', icon: BarChart3 },
            { id: 'system', label: 'System', icon: Server },
            { id: 'aiusage', label: 'AI Usage', icon: Sparkles },
            { id: 'test', label: 'Test', icon: FlaskConical }
          ].map(tab => {
            const Icon = tab.icon;
            const on = activeSection === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSection(tab.id)}
                className={`flex items-center gap-1.5 px-[18px] py-2.5 text-[13.5px] -mb-px border-b-2 whitespace-nowrap ${
                  on
                    ? 'text-carbon-ink font-semibold border-carbon-blue-60'
                    : 'text-carbon-gray-50 border-transparent hover:text-carbon-ink'
                }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Lookup Tables Sub-tabs — ink filter chips */}
        {activeSection === 'lookups' && (
          <div className="mt-3 flex flex-wrap gap-1">
            {Object.entries(LOOKUP_TABLES).map(([key, table]) => (
              <button
                key={key}
                onClick={() => setSelectedTable(key)}
                className={`px-2.5 py-1 font-plex-mono text-[10.5px] tracking-[0.04em] uppercase font-medium border ${
                  selectedTable === key
                    ? 'bg-carbon-ink text-carbon-white border-carbon-ink'
                    : 'bg-carbon-white text-carbon-gray-70 border-carbon-gray-30 hover:bg-carbon-gray-10'
                }`}
              >
                {table.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      {activeSection === 'lookups' && (
        <LookupTableManager tableKey={selectedTable} tenantId={resolvedTenantId} />
      )}

      {activeSection === 'roles' && (
        <RolesAndUsersPage tenantId={resolvedTenantId} />
      )}

      {/* CHA-632 (W7b) — the tenant Directory (people the tenant works with, job-function tags, groups),
          below the ACL screen above. Admins only, as the gateway enforces; tenant from the shell. */}
      {activeSection === 'roles' && canAdminTenant(userProfile) && (
        <DirectoryPanel call={callWorkGateway} />
      )}

      {activeSection === 'routing' && (
        <RoutingAdminConfig />
      )}

      {activeSection === 'bidata' && (
        <BIDataSettings />
      )}

      {activeSection === 'system' && (
        <SystemSettings userProfile={userProfile} />
      )}

      {/* CHA-141 — AI calls made with the tenant's key: usage and estimated cost from the CHA-140 ledger.
          Admins only, as the ledger's rules enforce; tenant from the shell. */}
      {activeSection === 'aiusage' && canAdminTenant(userProfile) && (
        <AiUsagePanel tenantId={resolvedTenantId} />
      )}

      {activeSection === 'test' && (
        <EmailTestSection />
      )}
    </div>
  );
}

// ============================================================================
// EMAIL TEST SECTION - Process, Rules, Templates, Distribution Settings
// ============================================================================
function EmailTestSection() {
  const [activeEmailTab, setActiveEmailTab] = useState('process');

  const emailTabs = [
    { id: 'process', label: 'Process', icon: Play },
    { id: 'rules', label: 'Rules', icon: Filter },
    { id: 'templates', label: 'Templates', icon: FileText },
    { id: 'distribution', label: 'Distribution Settings', icon: Users }
  ];

  return (
    <div className="space-y-5 font-plex text-carbon-ink">
      {/* Email Section Header */}
      <div className="bg-carbon-white border border-carbon-border overflow-hidden">
        <div className="px-[18px] py-3 bg-carbon-gray-10 border-b border-carbon-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-carbon-white border border-carbon-border text-carbon-ink">
              <Mail className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-carbon-ink m-0">Email Automation</h2>
              <p className="text-xs text-carbon-gray-50 m-0">Configure automated email reports and notifications</p>
            </div>
          </div>
        </div>

        {/* Email Sub-tabs */}
        <div className="flex border-b border-carbon-border bg-carbon-gray-10">
          {emailTabs.map((tab) => {
            const Icon = tab.icon;
            const on = activeEmailTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveEmailTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 text-[12.5px] -mb-px border-b-2 ${
                  on
                    ? 'text-carbon-ink font-semibold border-carbon-blue-60 bg-carbon-white'
                    : 'text-carbon-gray-50 border-transparent hover:text-carbon-ink'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Email Tab Content */}
        <div className="p-6">
          {activeEmailTab === 'process' && <EmailProcessPanel />}
          {activeEmailTab === 'rules' && <EmailRulesPanel />}
          {activeEmailTab === 'templates' && <EmailTemplatesPanel />}
          {activeEmailTab === 'distribution' && <EmailDistributionPanel />}
        </div>
      </div>
    </div>
  );
}

// Email Process Panel - Run/trigger, enable/disable, status
function EmailProcessPanel() {
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [enabled, setEnabled] = useState(true);

  const handleRunNow = async () => {
    setRunning(true);
    // TODO: Call the Step 8 email function
    setTimeout(() => {
      setRunning(false);
      setLastRun(new Date().toISOString());
    }, 2000);
  };

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <div className="text-sm text-slate-500 mb-1">Status</div>
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${enabled ? 'bg-green-500' : 'bg-slate-400'}`} />
            <span className="font-medium text-slate-700">{enabled ? 'Enabled' : 'Disabled'}</span>
          </div>
        </div>
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <div className="text-sm text-slate-500 mb-1">Last Run</div>
          <div className="font-medium text-slate-700">
            {lastRun ? new Date(lastRun).toLocaleString() : 'Never'}
          </div>
        </div>
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <div className="text-sm text-slate-500 mb-1">Schedule</div>
          <div className="font-medium text-slate-700">Manual (After Step 7)</div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleRunNow}
          disabled={running || !enabled}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
        >
          {running ? <Loader2 className="h-5 w-5 animate-spin" /> : <Play className="h-5 w-5" />}
          {running ? 'Running Step 8...' : 'Run Step 8 Now'}
        </button>

        <label className="flex items-center gap-3 cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="sr-only"
            />
            <div className={`w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-blue-600' : 'bg-slate-300'}`}>
              <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${enabled ? 'translate-x-5' : ''}`} />
            </div>
          </div>
          <span className="text-sm text-slate-600">Enable automated emails</span>
        </label>
      </div>

      {/* Reports to Send */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <h3 className="font-medium text-slate-700">Reports Included in Step 8</h3>
        </div>
        <div className="divide-y divide-slate-100">
          {[
            { name: 'Costing Errors', description: 'POs with missing or invalid cost data', enabled: true },
            { name: 'Late to Ship (Open Issued)', description: 'Orders 14+ days past ship date', enabled: true },
            { name: 'Late to Receive (Open Shipped)', description: 'Orders 14+ days past due date', enabled: true }
          ].map((report, idx) => (
            <div key={idx} className="flex items-center justify-between px-4 py-3">
              <div>
                <div className="font-medium text-slate-700">{report.name}</div>
                <div className="text-sm text-slate-500">{report.description}</div>
              </div>
              <div className={`px-2 py-1 rounded text-xs font-medium ${report.enabled ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                {report.enabled ? 'Included' : 'Disabled'}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Email Rules Panel - Filter/exclusion config
function EmailRulesPanel() {
  const [rules, setRules] = useState([
    { id: 1, name: 'Exclude Test POs', field: 'PO_Number', operator: 'starts_with', value: 'TEST', active: true },
    { id: 2, name: 'Min Days Late', field: 'Days_Late', operator: 'greater_than', value: '7', active: false }
  ]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Define rules to filter or exclude data from email reports</p>
        <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
          <Plus className="h-4 w-4" />
          Add Rule
        </button>
      </div>

      {/* Rules Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Rule Name</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Field</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Operator</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Value</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">Active</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rules.map((rule) => (
              <tr key={rule.id} className="hover:bg-slate-50/50">
                <td className="px-4 py-3 text-slate-700">{rule.name}</td>
                <td className="px-4 py-3 text-slate-600 font-mono text-xs">{rule.field}</td>
                <td className="px-4 py-3 text-slate-600">{rule.operator.replace('_', ' ')}</td>
                <td className="px-4 py-3 text-slate-600">{rule.value}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${rule.active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {rule.active ? 'Yes' : 'No'}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <button className="text-slate-400 hover:text-blue-600 p-1"><Edit3 className="h-4 w-4" /></button>
                  <button className="text-slate-400 hover:text-red-600 p-1"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-400">
        Rules are applied in order. Use drag and drop to reorder (coming soon).
      </div>
    </div>
  );
}

// Email Templates Panel - Email content per report type
function EmailTemplatesPanel() {
  const [selectedReport, setSelectedReport] = useState('costing_errors');
  const [template, setTemplate] = useState({
    subject: 'Production Report: {{reportType}} for {{managerName}}',
    greeting: 'Hi {{managerName}},',
    body: 'Please find attached your {{reportType}} report.',
    footer: 'Best regards,\nInMocean Operations Team'
  });

  const reports = [
    { id: 'costing_errors', name: 'Costing Errors' },
    { id: 'late_to_ship', name: 'Late to Ship' },
    { id: 'late_to_receive', name: 'Late to Receive' }
  ];

  return (
    <div className="space-y-6">
      {/* Report Type Selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-500">Template for:</span>
        <select
          value={selectedReport}
          onChange={(e) => setSelectedReport(e.target.value)}
          className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
        >
          {reports.map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
      </div>

      {/* Template Editor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Subject Line</label>
            <input
              type="text"
              value={template.subject}
              onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Greeting</label>
            <input
              type="text"
              value={template.greeting}
              onChange={(e) => setTemplate({ ...template, greeting: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Body</label>
            <textarea
              value={template.body}
              onChange={(e) => setTemplate({ ...template, body: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Footer</label>
            <textarea
              value={template.footer}
              onChange={(e) => setTemplate({ ...template, footer: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
            />
          </div>
          <div className="text-xs text-slate-400">
            Available variables: {'{{managerName}}'}, {'{{managerCode}}'}, {'{{reportType}}'}, {'{{recordCount}}'}, {'{{reportDate}}'}
          </div>
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
            <Save className="h-4 w-4" />
            Save Template
          </button>
        </div>

        {/* Preview */}
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <div className="text-sm font-medium text-slate-500 mb-3">Preview</div>
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="bg-blue-600 text-white px-4 py-3">
              <h3 className="font-medium">InMocean Production Report</h3>
            </div>
            <div className="p-4 text-sm text-slate-600 space-y-3">
              <p>{template.greeting.replace('{{managerName}}', 'John Smith')}</p>
              <p>{template.body.replace('{{reportType}}', 'Costing Errors')}</p>
              <div className="bg-slate-50 border-l-4 border-blue-500 p-3 text-xs">
                <p><strong>Report Type:</strong> Costing Errors</p>
                <p><strong>Manager Code:</strong> JS-001</p>
                <p><strong>Total Records:</strong> 15</p>
              </div>
              <p className="whitespace-pre-line text-slate-500">{template.footer}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Email Distribution Panel - Link to distribution list, per-manager settings
function EmailDistributionPanel() {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchManagers = async () => {
      try {
        const data = await RealLookupService.getTableData('email_distribution_list');
        setManagers(data);
      } catch (error) {
        console.error('Error fetching managers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchManagers();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-500">Loading distribution list...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Manage who receives automated email reports</p>
        <button
          onClick={() => {/* Navigate to email_distribution_list lookup table */}}
          className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 text-sm font-medium"
        >
          <Settings className="h-4 w-4" />
          Manage in Lookup Tables
        </button>
      </div>

      {/* Distribution Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
          <div className="text-2xl font-bold text-blue-600">{managers.length}</div>
          <div className="text-sm text-blue-600">Total Recipients</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-100">
          <div className="text-2xl font-bold text-green-600">{managers.filter(m => m.active === true || m.active === 'true').length}</div>
          <div className="text-sm text-green-600">Active</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
          <div className="text-2xl font-bold text-purple-600">{managers.filter(m => m.role === 'PO Manager').length}</div>
          <div className="text-sm text-purple-600">PO Managers</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4 border border-orange-100">
          <div className="text-2xl font-bold text-orange-600">{managers.filter(m => m.role === 'Executive').length}</div>
          <div className="text-sm text-orange-600">Executives</div>
        </div>
      </div>

      {/* Recipients Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Name</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Role</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Code</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Email</th>
              <th className="px-4 py-3 text-center font-medium text-slate-600">Active</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {managers.map((manager, idx) => (
              <tr key={manager.id || idx} className="hover:bg-slate-50/50">
                <td className="px-4 py-3 text-slate-700 font-medium">{manager.name}</td>
                <td className="px-4 py-3 text-slate-600">{manager.role}</td>
                <td className="px-4 py-3 text-slate-600 font-mono text-xs">{manager.code}</td>
                <td className="px-4 py-3 text-slate-600">{manager.direct_email}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    manager.active === true || manager.active === 'true'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-slate-100 text-slate-500'
                  }`}>
                    {manager.active === true || manager.active === 'true' ? 'Yes' : 'No'}
                  </span>
                </td>
              </tr>
            ))}
            {managers.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                  No recipients configured. Add them in Lookup Tables → Email Distribution List.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
